import { apiClient } from './api'

export interface CreateCredentialData {
  type: 'ciec' | 'efirma'
  rfc: string
  password?: string
  certificate?: string
  privateKey?: string
}

export interface SyntageCredential {
  id: string
  userId: string
  rfc: string
  name?: string
  type: 'ciec' | 'efirma'
  status: 'pending' | 'valid' | 'invalid' | 'error'
  isValid: boolean
  syntageId?: string
  syntageResource?: string
  attemptNumber: number
  errorMessage?: string
  validatedAt?: string
  invalidatedAt?: string
  createdAt: string
  updatedAt: string
  metadata?: any
}

export interface CreateCredentialResponse {
  success: boolean
  message: string
  credential: SyntageCredential
  fromCache: boolean
}

export interface GetCredentialStatsResponse {
  success: boolean
  stats: {
    total: number
    pending: number
    valid: number
    invalid: number
    error: number
    successRate: string
  }
}

export interface GetMyCredentialsResponse {
  success: boolean
  userId: string
  totalAttempts: number
  history: SyntageCredential[]
}

export interface GetValidCredentialResponse {
  success: boolean
  userId: string
  hasValidCredential: boolean
  credential?: SyntageCredential
}

/**
 * Service for handling Syntage credential management
 */
class SyntageService {
  private baseURL = '/syntage/credentials'

  /**
   * Create a new credential for CIEC or E.Firma
   */
  async createCredential(
    data: CreateCredentialData
  ): Promise<CreateCredentialResponse> {
    try {
      const response = await apiClient.post<CreateCredentialResponse>(
        this.baseURL,
        data
      )

      return response.data
    } catch (error: any) {
      console.error('Error creating credential:', error)
      throw new Error(
        error.response?.data?.message ||
          'Error al crear las credenciales. Intenta nuevamente.'
      )
    }
  }

  /**
   * Get the current user's credential history
   */
  async getMyCredentials(): Promise<GetMyCredentialsResponse> {
    try {
      const response = await apiClient.get<any>(
        `${this.baseURL}/my-credentials`
      )
      // Handle wrapped response structure
      const data = response.data.data || response.data
      return data
    } catch (error: any) {
      console.error('Error getting credential history:', error)
      throw new Error(
        error.response?.data?.message ||
          'Error al obtener el historial de credenciales.'
      )
    }
  }

  /**
   * Get the current user's valid credential
   */
  async getMyValidCredential(): Promise<GetValidCredentialResponse> {
    try {
      const response = await apiClient.get<any>(
        `${this.baseURL}/my-valid-credential`
      )
      // Handle wrapped response structure
      const data = response.data.data || response.data
      return data
    } catch (error: any) {
      console.error('Error getting valid credential:', error)
      throw new Error(
        error.response?.data?.message ||
          'Error al obtener la credencial válida.'
      )
    }
  }

  /**
   * Get credential statistics (admin function)
   */
  async getCredentialStats(): Promise<GetCredentialStatsResponse> {
    try {
      const response = await apiClient.get<GetCredentialStatsResponse>(
        `${this.baseURL}/stats`
      )
      return response.data
    } catch (error: any) {
      console.error('Error getting credential stats:', error)
      throw new Error(
        error.response?.data?.message ||
          'Error al obtener las estadísticas de credenciales.'
      )
    }
  }

  /**
   * Check if user has a valid credential
   */
  async hasValidCredential(): Promise<boolean> {
    try {
      const response = await this.getMyValidCredential()
      return response.hasValidCredential
    } catch (error) {
      console.error('Error checking valid credential:', error)
      return false
    }
  }

  /**
   * Poll for credential validation status
   * Useful for waiting for webhook updates
   */
  async pollCredentialStatus(
    intervalMs: number = 2000,
    maxAttempts: number = 30
  ): Promise<SyntageCredential | null> {
    let attempts = 0

    // First, check if we already have a valid credential immediately
    try {
      const response = await this.getMyValidCredential()
      if (response.hasValidCredential && response.credential) {
        console.log('Valid credential already exists, returning immediately')
        return response.credential
      }
    } catch (error) {
      console.error('Error checking initial credential status:', error)
    }

    return new Promise(resolve => {
      const interval = setInterval(async () => {
        attempts++

        try {
          const response = await this.getMyValidCredential()

          // If we have a valid credential, return it
          if (response.hasValidCredential && response.credential) {
            clearInterval(interval)
            resolve(response.credential)
            return
          }

          // Check if we have any recent credential that failed
          const history = await this.getMyCredentials()
          const latestCredential = history.history[0]

          if (
            latestCredential &&
            (latestCredential.status === 'invalid' ||
              latestCredential.status === 'error')
          ) {
            clearInterval(interval)
            resolve(latestCredential)
            return
          }

          // If the credential is already valid but not detected above, return it
          if (latestCredential && latestCredential.status === 'valid') {
            clearInterval(interval)
            resolve(latestCredential)
            return
          }

          // If we've reached max attempts, stop polling
          if (attempts >= maxAttempts) {
            clearInterval(interval)
            // Return the latest credential even if it's pending
            resolve(latestCredential || null)
          }
        } catch (error) {
          console.error('Error polling credential status:', error)
          clearInterval(interval)
          resolve(null)
        }
      }, intervalMs)
    })
  }
}

export const syntageService = new SyntageService()
export default syntageService
